import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Shop/shop.css';
import Login from './Login';
import { v4 as uuidv4 } from 'uuid';

// ===========================================
// GUEST CART SERVICE - SAME AS Shop.jsx
// ===========================================
const GUEST_CART_KEY = 'guest_cart';

const sendPurchaseEvent = async (value) => {
  const eventId = uuidv4();

  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      value: value,
      currency: 'INR'
    }, {
      eventID: eventId
    });
  }

  await fetch('https://evercorenutrition.com/ev_admin/api/fb-event/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      event_id: eventId,
      value: value
    })
  });
};

const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error getting guest cart:', error);
    return [];
  }
};

const saveGuestCart = (cartItems) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
  } catch (error) {
    console.error('Error saving guest cart:', error);
  }
};

// Add combo to guest cart - SAME STRUCTURE as products
const addToGuestCart = (comboId, quantity, comboDetails) => {
  const guestCart = getGuestCart();
  
  // Check if combo already exists in cart (using combo_id and item_type)
  const existingItemIndex = guestCart.findIndex(
    item => item.combo_id === comboId && item.item_type === 'combo'
  );
  
  if (existingItemIndex !== -1) {
    // Update quantity if already exists
    guestCart[existingItemIndex].quantity += quantity;
    // console.log('Updated existing combo in guest cart:', guestCart[existingItemIndex]);
  } else {
    // Add new combo item
    guestCart.push({
      combo_id: comboId,
      quantity: quantity,
      name: comboDetails.name,
      unit_price: parseFloat(comboDetails.selling_price),
      price: parseFloat(comboDetails.selling_price),
      image: comboDetails.image_url || comboDetails.image,
      added_at: new Date().toISOString(),
      item_type: 'combo'  // Important: Distinguish from regular products
    });
    // console.log('Added new combo to guest cart:', { comboId, quantity, comboDetails });
  }
  
  saveGuestCart(guestCart);
  // console.log('Guest cart after adding combo:', getGuestCart());
  return guestCart;
};

// Merge guest cart with user cart - Handles both products AND combos
const mergeGuestCartWithUserCart = async (customerId, token) => {
  const guestCart = getGuestCart();
  
  if (!guestCart || guestCart.length === 0) {
    return { success: true, message: 'No guest cart to merge' };
  }
  
  // console.log('Merging guest cart:', guestCart);
  let mergedCount = 0;
  
  try {
    for (const item of guestCart) {
      let body = {};
      
      if (item.item_type === 'combo') {
        // This is a combo item
        body = {
          customer_id: String(customerId),
          combo_id: item.combo_id,
          quantity: item.quantity
        };
        // console.log('Merging combo:', body);
      } else {
        // This is a regular product
        body = {
          customer_id: String(customerId),
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity
        };
        // console.log('Merging product:', body);
      }
      
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/cart/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        mergedCount++;
      } else {
        console.error('Failed to merge item:', await response.text());
      }
    }
    
    // Clear guest cart after successful merge
    localStorage.removeItem(GUEST_CART_KEY);
    // console.log(`Merged ${mergedCount} items from guest cart`);
    
    return { success: true, message: `Merged ${mergedCount} items from guest cart` };
  } catch (error) {
    console.error('Error merging guest cart:', error);
    return { success: false, message: 'Failed to merge guest cart' };
  }
};

const ComboCard = ({ 
  combo, 
  onAddToCart, 
  isAuthenticated, 
  showWarning, 
  showLoginPopup, 
  setShowLoginPopup, 
  handleLoginSuccess,
  addingToCart,
  setAddingToCart
}) => {
  const navigate = useNavigate();

  // Check if combo is in stock
  const isComboInStock = () => {
    return combo.status === 'active' && combo.is_active === true;
  };

  const inStock = isComboInStock();
  const isLoading = addingToCart && addingToCart[combo.id];

  // Get prices from API response
  const originalTotal = parseFloat(combo.mrp) || 0;
  const sellingTotal = parseFloat(combo.selling_price) || 0;
  const discount = combo.discount_percentage || parseFloat(combo.discount) || 0;
  const savings = originalTotal - sellingTotal;

  // Check for bestseller and most popular tags
  const isBestseller = combo.is_bestseller === true;
  const isMostPopular = combo.is_most_popular === true;

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    // Prevent double click
    if (isLoading) return;
    
    if (!inStock) {
      showWarning('This combo is currently out of stock', combo.id);
      return;
    }
    
    // Set loading state for this combo
    if (setAddingToCart) {
      setAddingToCart(prev => ({ ...prev, [combo.id]: true }));
    }
    
    await onAddToCart(combo);
    
    // Clear loading state after a delay
    setTimeout(() => {
      if (setAddingToCart) {
        setAddingToCart(prev => ({ ...prev, [combo.id]: false }));
      }
    }, 500);
  };

  const handleComboClick = () => {
    navigate(`/combo/${combo.id}`);
  };

  const getComboImage = () => {
    if (combo.image_url) return combo.image_url;
    if (combo.image) return combo.image;
    return '/assets/images/default-combo.png';
  };

  return (
    <div style={{ width: '100%', padding: 0, margin: 0, boxShadow: "1px 2px 15px #868585", borderRadius: "10px" }}>
      <div
        onClick={handleComboClick}
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          cursor: 'pointer',
          position: 'relative',
          opacity: inStock ? 1 : 0.8
        }}
        onMouseEnter={(e) => {
          if (window.innerWidth > 768 && inStock) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
        }}
      >
        {/* Stock Status Badge */}
        {!inStock && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            backgroundColor: '#ff4444',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            zIndex: 3,
            boxShadow: '0 2px 8px rgba(255,68,68,0.3)'
          }}>
            <i className="fa-solid fa-clock" style={{ marginRight: '5px' }}></i>
            Out of Stock
          </div>
        )}

        {/* BESTSELLER / MOST POPULAR BADGE */}
        {(isBestseller || isMostPopular) && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            backgroundColor: '#ff9800',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '700',
            zIndex: 3,
            boxShadow: '0 2px 8px rgba(255,152,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            letterSpacing: '0.5px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="gold" stroke="gold" strokeWidth="0">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            {isMostPopular ? 'MOST POPULAR' : 'BESTSELLER'}
          </div>
        )}

        {/* Image Section - SAME AS SHOP COMPONENT */}
        <div style={{ 
          position: 'relative', 
          width: '100%',
          aspectRatio: '1/1',
          overflow: 'hidden', 
          backgroundColor: '#f0f0f0' 
        }}>
          <img
            src={getComboImage()}
            alt={combo.name}
            style={{ 
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              padding: '0',
              transition: 'transform 0.3s ease',
              filter: inStock ? 'none' : 'grayscale(50%)'
            }}
            onMouseEnter={(e) => { if (window.innerWidth > 768 && inStock) e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          />
        </div>

        {/* Content Section - SAME AS SHOP COMPONENT */}
        <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Combo Name */}
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0 0 8px 0', color: '#333', lineHeight: '1.4' }}>
            <span style={{ color: '#333', textDecoration: 'none' }}>
              {combo.name || 'Combo Offer'}
            </span>
          </h3>

          {/* Description */}
          {combo.description && (
            <p style={{ fontSize: '0.9rem', color: '#666', margin: '0 0 10px 0' }}>
              {combo.description.length > 80 ? combo.description.substring(0, 80) + '...' : combo.description}
            </p>
          )}

          {/* Pack/Item Count */}
          <p style={{ fontSize: '0.9rem', color: '#666', margin: '0 0 10px 0' }}>
            {combo.item_count || combo.no_of_packs} {combo.item_count === 1 ? 'Item' : 'Items'} in this combo
          </p>

          {/* Price Section - SAME AS SHOP COMPONENT */}
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
            <span style={{ fontSize: '1.3rem', fontWeight: '700', color: '#333' }}>
              ₹{sellingTotal.toLocaleString('en-IN')}
            </span>
            {originalTotal > sellingTotal && (
              <span style={{ fontSize: '0.9rem', color: '#999', textDecoration: 'line-through', marginLeft: '8px' }}>
                ₹{originalTotal.toLocaleString('en-IN')}
              </span>
            )}
            {discount > 0 && (
              <>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '0.75rem',
                  color: '#fff',
                  backgroundColor: '#e53935',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  marginLeft: '4px',
                  fontWeight: '700',
                  whiteSpace: 'nowrap'
                }}>
                  {Math.floor(discount)}% OFF
                </span>
                {originalTotal > sellingTotal && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.75rem',
                    color: '#2e7d32',
                    backgroundColor: '#e8f5e9',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    marginLeft: '4px',
                    fontWeight: '700',
                    border: '1px solid #a5d6a7',
                    whiteSpace: 'nowrap'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                      <line x1="7" y1="7" x2="7.01" y2="7"/>
                    </svg>
                    Save ₹{Math.floor(savings).toLocaleString('en-IN')}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Add to Cart Button - SAME AS SHOP COMPONENT */}
          <button
            onClick={handleAddToCart}
            disabled={!inStock || isLoading}
            style={{ 
              width: '100%',
              padding: '10px 12px', 
              backgroundColor: !inStock ? '#999' : (isLoading ? '#666' : '#000'), 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontSize: '0.9rem', 
              fontWeight: '600', 
              cursor: (!inStock || isLoading) ? 'not-allowed' : 'pointer', 
              transition: 'background-color 0.2s ease',
              opacity: (!inStock || isLoading) ? 0.7 : 1,
              marginTop: 'auto'
            }}
            onMouseEnter={(e) => { 
              if (window.innerWidth > 768 && inStock && !isLoading) {
                e.currentTarget.style.backgroundColor = '#333'; 
              }
            }}
            onMouseLeave={(e) => { 
              if (inStock && !isLoading) {
                e.currentTarget.style.backgroundColor = '#000'; 
              }
            }}
          >
            {isLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px', marginRight: '5px' }}></span>
            ) : null}
            {!inStock ? 'Out of Stock' : (isLoading ? 'Adding...' : 'Add to Cart')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Combo Component
const ComboSection = ({ title = "Combo Offers", subtitle = "Save more with our special combos", useStaticData = false }) => {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [addingToCart, setAddingToCart] = useState({});

  const isAuthenticated = () => !!localStorage.getItem('authToken');

  const getCustomerId = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.id || parsed.customer_id || null;
      }
    } catch (e) {}
    return null;
  };

  const showWarningMessage = (message, productId = null) => {
    setWarningMessage(message);
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 3000);
  };

  // Handle login success - with guest cart merge
  const handleLoginSuccess = useCallback(async (user) => {
    setShowLoginPopup(false);
    
    // Merge guest cart with user cart
    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();
    
    if (token && customerId) {
      const mergeResult = await mergeGuestCartWithUserCart(customerId, token);
      if (mergeResult.success && mergeResult.message !== 'No guest cart to merge') {
        showWarningMessage(mergeResult.message);
      }
    }
    
    window.dispatchEvent(new Event('cartUpdated'));
    showWarningMessage('Login successful!');
  }, []);

  // Fetch combos from API
  useEffect(() => {
    const fetchCombos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('https://evercorenutrition.com/ev_admin/api/combos/', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Handle the API response structure
        let combosList = [];
        if (data.results && Array.isArray(data.results)) {
          combosList = data.results;
        } else if (Array.isArray(data)) {
          combosList = data;
        }
        
        // Filter only active combos
        const activeCombos = combosList.filter(combo => combo.status === 'active' && combo.is_active === true);
        
        setCombos(activeCombos);
      } catch (err) {
        console.error('Error fetching combos:', err);
        setError(err.message || 'Failed to fetch combos');
        
        // If API fails and useStaticData is true, fallback to static data
        if (useStaticData) {
          // console.log('Falling back to static combo data');
          const staticCombos = [
            {
              id: 1,
              combo_id: "CMB-DEP6UUC9",
              name: "Gym Starter Pack",
              image_url: "/assets/images/combo-starter.jpg",
              mrp: "8999.00",
              selling_price: "6999.00",
              discount_percentage: 22.2,
              item_count: 4,
              no_of_packs: 4,
              status: "active",
              is_active: true,
              description: "Complete nutrition bundle for beginners"
            },
            {
              id: 2,
              combo_id: "CMB-ZGMQAICU",
              name: "Mass Gainer Combo",
              image_url: "/assets/images/combo-mass.jpg",
              mrp: "7499.00",
              selling_price: "5999.00",
              discount_percentage: 20,
              item_count: 3,
              no_of_packs: 3,
              status: "active",
              is_active: true,
              description: "Perfect for hard gainers and muscle building"
            },
            {
              id: 3,
              combo_id: "CMB-EXAMPLE1",
              name: "Weight Loss Bundle",
              image_url: "/assets/images/combo-weightloss.jpg",
              mrp: "6499.00",
              selling_price: "4999.00",
              discount_percentage: 23.1,
              item_count: 3,
              no_of_packs: 3,
              status: "active",
              is_active: true,
              description: "Fat burner & lean protein combo"
            }
          ];
          setCombos(staticCombos);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCombos();
  }, [useStaticData]);

  // Add combo to cart with proper guest cart support
  const addComboToCart = async (combo) => {
    // console.log('Adding combo to cart:', combo);

    await sendPurchaseEvent(parseFloat(combo.selling_price));
    
    // If not authenticated, save to guest cart
    if (!isAuthenticated()) {
      // console.log('User not authenticated, saving to guest cart');
      const comboDetailsForCart = {
        name: combo.name,
        selling_price: parseFloat(combo.selling_price),
        image_url: combo.image_url || combo.image
      };
      
      addToGuestCart(combo.id, 1, comboDetailsForCart);
      showWarningMessage('Combo added to cart!');
      window.dispatchEvent(new Event('cartUpdated'));
      return;
    }

    // User is authenticated, add to server cart
    // console.log('User authenticated, adding to server cart');
    const token = localStorage.getItem('authToken');
    const customerId = getCustomerId();

    if (!customerId) {
      showWarningMessage('Could not identify customer. Please login again.');
      return;
    }

    try {
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/cart/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          combo_id: combo.id,
          quantity: 1,
        }),
      });

      if (response.ok) {
        showWarningMessage('Combo added to cart!');
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        showWarningMessage(errorData.message || errorData.error || 'Failed to add combo to cart.');
      }
    } catch (err) {
      console.error('Add combo to cart error:', err);
      showWarningMessage('Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="combo-section" style={{ padding: '40px 0' }}>
        <div className="container-fluid-lg">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading combos...</span>
            </div>
            <p className="mt-2 text-muted">Loading amazing combo offers...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && combos.length === 0) {
    return (
      <div className="combo-section" style={{ padding: '40px 0' }}>
        <div className="container-fluid-lg">
          <div className="text-center py-5">
            <div className="alert alert-danger" role="alert">
              <i className="fa-solid fa-circle-exclamation me-2"></i>
              Error loading combos: {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (combos.length === 0) {
    return null;
  }

  return (
    <>
      {/* Toast Notification */}
      {showWarning && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px',
          backgroundColor: warningMessage.includes('added') || warningMessage.includes('removed') ? '#4CAF50' : '#ff4444',
          color: 'white', padding: '15px 25px', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: '10px', animation: 'slideIn 0.3s ease'
        }}>
          <i className={`fa-${warningMessage.includes('added') || warningMessage.includes('removed') ? 'regular fa-circle-check' : 'solid fa-circle-exclamation'}`}></i>
          <span>{warningMessage}</span>
          <button onClick={() => setShowWarning(false)} style={{ background: 'none', border: 'none', color: 'white', marginLeft: '10px', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}

      <section className="combo-section section-b-space" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              {/* Section Header */}
              <div className="text-center mb-5">
                <h2 style={{ 
                  fontSize: '2rem', 
                  fontWeight: '700', 
                  marginBottom: '10px',
                  color: '#333',
                  position: 'relative',
                  display: 'inline-block'
                }}>
                  {title}
                </h2>
                {subtitle && (
                  <p style={{ fontSize: '1rem', color: '#666', marginTop: '10px' }}>
                    {subtitle}
                  </p>
                )}
                <div style={{
                  width: '60px',
                  height: '3px',
                  backgroundColor: '#000000',
                  margin: '15px auto 0',
                  borderRadius: '2px'
                }}></div>
              </div>

              {/* Products Grid - SAME AS SHOP COMPONENT */}
              <div className="row" style={{ margin: 0 }}>
                <div className="col-12" style={{ padding: 0 }}>
                  <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr', width: '100%', margin: 0, padding: 0 }}>
                    {combos.map((combo) => (
                      <ComboCard
                        key={combo.id}
                        combo={combo}
                        onAddToCart={addComboToCart}
                        isAuthenticated={isAuthenticated}
                        showWarning={showWarningMessage}
                        showLoginPopup={showLoginPopup}
                        setShowLoginPopup={setShowLoginPopup}
                        handleLoginSuccess={handleLoginSuccess}
                        addingToCart={addingToCart}
                        setAddingToCart={setAddingToCart}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Login Popup Modal */}
        {showLoginPopup && (
          <div className="login-popup-overlay" onClick={() => setShowLoginPopup(false)}>
            <div className="login-popup-container" onClick={(e) => e.stopPropagation()}>
              <button className="login-popup-close" onClick={() => setShowLoginPopup(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
              <Login setOpen={setShowLoginPopup} onLoginSuccess={handleLoginSuccess} />
            </div>
          </div>
        )}
      </section>

      <style>{`
        @media (min-width: 768px) {
          .combo-section div[style*="display: grid"][style*="gap: 20px"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 1200px) {
          .combo-section div[style*="display: grid"][style*="gap: 20px"] {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        .combo-section .container-fluid-lg {
          padding-left: 15px !important;
          padding-right: 15px !important;
        }
        @media (min-width: 768px) {
          .combo-section .container-fluid-lg { padding-left: 20px !important; padding-right: 20px !important; }
        }
        @media (min-width: 1200px) {
          .combo-section .container-fluid-lg { padding-left: 30px !important; padding-right: 30px !important; }
        }
        .combo-section .row { margin-left: 0 !important; margin-right: 0 !important; }
        .combo-section .col-12 { padding-left: 0 !important; padding-right: 0 !important; }
        
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Login Popup Styles */
        .login-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.3s ease;
        }

        .login-popup-container {
          position: relative;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          background: transparent;
          border-radius: 20px;
          animation: slideUp 0.3s ease;
        }

        .login-popup-close {
          position: absolute;
          top: 15px;
          right: 15px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          font-size: 16px;
          color: #333;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .login-popup-close:hover {
          background: #f0f0f0;
          transform: scale(1.1);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default ComboSection;